import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from "react-router-dom";
import styled from 'styled-components'
import { Amount, Stat, Button, Panel, Layout, LazyBoi, GateKeeper, Field } from '@components'
import { Asset } from '@archetypes'
import appController, { Status } from '@app/App.Controller'
import { fromWei, toWei, BN } from "@util/helpers"
import { ReactComponent as Refresh } from '@assets/Icon_Refresh.svg';
import { ReactComponent as IconWarning } from '@assets/Icon_Warning.svg';
import { WithdrawController } from './_controller'


const Wrapper = styled.div`

	margin-top: 40px;

	.group-input {
		margin-bottom: 40px;
	}

	${props => !props.visible && "display: none;"}

`

const Main = styled(
	({ address, setExtensions, ...rest }) => {

		const { synthetics } = appController

		// approval params
		const [approvalStatus, setApprovalStatus] = useState()
		const [approvals, setApprovals] = useState()
		const [controller, setController] = useState()
		const [params, setParams] = useState()

		useEffect(() => {
			const withdrawController = new WithdrawController(address, appController)
			withdrawController.on('approvals', setApprovals)
			withdrawController.on('params', setParams)
			setController(withdrawController)
			withdrawController.init()

			return () => withdrawController.cleanup()
		}, []) // eslint-disable-line

		useEffect(() => {
			if (params) {
				let payload = []
				if (params?.myCollateral?.tokensOutstanding) {
					payload.push({
						key: "Total Debt",
						value: <LazyBoi
							value={params?.myCollateral?.tokensOutstanding}
							suffix={params?.debtSymbol}
							dp={2}
							fromWei
						/>
					})
				}

				if (params?.myCollateral?.collateral) {
					const newTotalCollateral = Number(fromWei(params?.myCollateral?.collateral)) - params?.amount
					payload.push({
						key: "Total Collateral",
						value: <LazyBoi
							value={newTotalCollateral}
							suffix={params?.symbol}
							dp={2}
						/>
					})
				}
				try {
					const collateral = (params.myPosition.collateral)
					const ratio = toWei(params.myPosition.actualCurrentRatio)
					const newRatio = BN(collateral).minus(BN(toWei(params.amount))).times(ratio).dividedBy(collateral)
					payload.push({
						key: "New Collateral Ratio (CR)",
						value: <LazyBoi value={fromWei(newRatio.toFixed(0)) * 100} suffix={"%"} dp={2} />
					})
				} catch (e) {

				}
				
				setExtensions(payload)
			}
		}, [params])

		const onSubmit = useCallback(() => {
			if (params?.amount !== 0) {
				if (params.fast) {
					synthetics.fastWithdraw(params?.tx)
				} else {
					synthetics.slowWithdrawSubmitRequest(params?.tx)
				}
			}
		}, [params])

		const waitingHours = (Number(params?.withdrawalLiveness) / 3600).toFixed(0)
 
		const getMaxCollateralSlowWithdrawal = (collateral, currentRatio) => {
			if (currentRatio && collateral) {
				const amount = (BN(1.5).times(collateral).dividedBy(currentRatio).minus(collateral).times(BN(-1)))
				return fromWei(amount.toFixed(0))
			}
			return 0
		}
 
		const max = params?.fast ? Number(fromWei(params?.max)) : getMaxCollateralSlowWithdrawal(params?.myCollateral?.collateral, params?.myPosition?.actualCurrentRatio)

		return <Panel fadeIn {...rest}>
			<GateKeeper
				controllerAssertions={[
					{
						controller: 'account',
						key: 'status',
						assert: val => val === Status.READY,
						onFail: <Panel fadeIn>Connect account to withdraw your assets</Panel>,
					},
				]}
				assertions={[
					{
						assert: () => !!params,
						onFail: () => <Panel fadeIn><Refresh className='rotate' />Fetching position information</Panel>,
					},
				]}
				dependencies={[params]}
			>

				<Field.RadioGroup
					title={'Select type of withdrawal'}
					value={params?.fast}
					options={[
						{
							label: 'Fast Withdrawal',
							sublabel: "Withdraw excess collateral and stay above GCR",
							value: true,
							disabled: params?.isOngoingWithdraw
						},
						{
							label: 'Slow Withdrawal (>' + waitingHours + ' hrs.)',
							sublabel: "Withdraw excess collateral below GCR",
							value: false,
						}
					]}
					onChange={val => controller?.updateFastWithdraw(val)}
				/>

				<Wrapper visible={!params?.isOngoingWithdraw}>
					<Amount.GroupSelect
						className="group-input"
						info="I WANT TO REDUCE"
						infoEnd={`Max : ${Number(max).toLocaleString()} ${params?.symbol}`}
						min={0}
						style={{ marginTop: 30 }}
						disabled={!controller}
						onChange={amount => controller?.updateInputAmount(amount)}
						max={Number(max)}
						value={params?.amount}
						selection={
							<Asset.Icon
								token={params?.symbol}
							/>
						}
					/>
				</Wrapper>
				{params?.fast ?
					(
						<Asset.ContractApproval.Wizard
							approvals={approvals}
							onUpdate={setApprovalStatus}
							actionText={"Reduce Collateral"}
							onAction={onSubmit}
						/>
					)
					:
					(
						<>
							{!params?.isOngoingWithdraw ?
								<>
									<LiquidationRiskWarning />
									<Asset.ContractApproval.Wizard
										approvals={approvals}
										onUpdate={setApprovalStatus}
										actionText={"Submit Requests"}
										onAction={onSubmit}
									/>
								</>
								:
								<>
									<Asset.Value.Withdraw
										requestTimestamp={params?.withdrawalRequestPassTimestamp}
										requestAmount={params?.withdrawalRequestAmount}
										address={address}
									/>
								</>
							}
						</>
					)
				}
				{/* <Field.RadioGroupVertical
					title={'Select type of withdrawal'}
					value={params?.fast}
					options={[
						{
							label: 'Fast Withdrawal',
							sublabel: "Withdraw excess collateral but remain above the Global Collateral Ratio (GCR)",
							value: true,
						},
						{
							label: 'Slow Withdrawal (>' + waitingHours + ' hrs.)',
							sublabel: "Withdraw collateral below Global Collateral Ratio (GCR). This. could take longer and you are at risk of liquidation if your Collateral Ratio <= Liquidation Ratio.",
							value: false,
						}
					]}
					onChange={val => controller?.updateFastWithdraw(val)}
				/>

				<Amount
					title='Amount to withdraw (PERL)'
					info={<LazyBoi prefix={'MAX: '} suffix={params?.symbol} value={max} />}
					className={'ratio'}
					onChange={val => controller?.updateInputAmount(val)}
					min={0}
					max={Number(max)}
					value={params?.amount}
					dp={2}
				/>

				<Panel.Divider light>
					<Asset.ContractApproval
						approvals={approvals}
						onUpdate={setApprovalStatus}
					/>
				</Panel.Divider>

				<Panel.Footer>
					<Panel.Footer.Group style={{ flexDirection: "row" }}>
						<Stat title='New Collaterization Ratio' small>
							<LazyBoi value={params?.newCRUsd ? params?.newCRUsd * 100 : 0} suffix={"%"} dp={2} />
						</Stat>
					</Panel.Footer.Group>
					<Panel.Footer.Group>
						{params?.fast ?
							(
								<Button.Primary
									onClick={() => synthetics.fastWithdraw(params?.tx)}
									disabled={(params?.amount === 0)}
								>
									Withdraw Collateral
								</Button.Primary>
							)
							:
							(
								<>
									<Button.Primary
										onClick={() => {
											return synthetics.slowWithdrawSubmitRequest(params?.tx)
										}}
										disabled={!params?.isOngoingWithdraw}
									>
										Submit Requests
							</Button.Primary>

									<Button.Primary
										onClick={() => {
											return synthetics.slowWithdrawCancelWithdrawal(params?.tx)
										}}
										disabled={params?.isOngoingWithdraw}
									>
										Cancel Requests
							</Button.Primary>
								</>
							)}
					</Panel.Footer.Group>
				</Panel.Footer> */}
			</GateKeeper>
		</Panel>

	})`
	
	`

const LiquidationRiskWarning = styled(
	({ className }) => {
		return (
			<div className={className}>
				<div>
					<IconWarning className="svg" />
				</div>
				<div>
					<h4>Liquidation Risk</h4>
					<p>{"You are reducing your collateral below the Global Collateral Ratio (GCR). This could take longer and put you at risk of liquidation if your Collateral Ratio <= Liquidation Ratio. Keep your collateral above GCR to avoid liquidation."}</p>
				</div>
			</div>
		)
	})`
	width: 100%; 
	background: #2D6AE2;
	color: white;
	padding: 20px;
	display: flex;
	flex-direction: row;
	font-size: 14px;
	border-radius: 4px;
	margin-bottom: 30px;

	.svg {
		width: 24px;
		height: 24px;
	}

	div {
		flex: 1;
		:last-child {
			flex: 10;
			h4 {
				font-size: 16px;
				font-weight: 600;
				color: white;
			}
		}
	}

	`

const Withdraw = ({ className }) => {
	const { address } = useParams();

	return <Layout back title='Withdraw Collateral' className={className}>
		<Layout.Aside>
			<Asset.Position address={address} />
			<Asset.Stats address={address} />
		</Layout.Aside>
		<Layout.Main>
			<Main address={address} />
		</Layout.Main>

	</Layout>
}

Withdraw.Panel = Main

export default Withdraw